import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { 
  Pickaxe, Layers, BarChart3, Trash2, Download, 
  Play, Check, Move 
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { CreatorLinks } from './components/CreatorLinks'
import { useDigTelemetry } from './hooks/useDigTelemetry'
import { SHARDS, getShard } from './lib/shards'
import { computeInterpretiveCharityScore, computeRitualPovertySig } from './lib/computeCharity'
import { 
  Shard, DigId, SiteId, DistanceVector, Interpretation, ClusterProposal, 
  NuanceTag, ALL_NUANCE_TAGS, SiteCalibration, CLUSTERING_PRINCIPLE 
} from './lib/types'
import { format } from 'date-fns'

// ===== Sovereign site identity (private trench key) =====
function getOrCreateSiteId(): SiteId {
  const key = 'strata_site_id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = 'st_' + Array.from(crypto.getRandomValues(new Uint8Array(11)))
      .map(b => b.toString(36).padStart(2, '0')).join('').slice(0, 22)
    localStorage.setItem(key, id)
  }
  return id as SiteId
}

// ===== Distance defaults =====
const DEFAULT_DIST: DistanceVector = { temporal: 5, spatial: 5, social: 5, hypothetical: 5 }

function clamp(n: number) { return Math.max(0, Math.min(10, Math.round(n))) }

// ===== Main App =====
export default function App() {
  const siteId = useMemo(() => getOrCreateSiteId(), [])
  const [currentView, setCurrentView] = useState<'hero' | 'trench' | 'archive' | 'about' | 'controls'>('hero')

  // Active dig state (the heart of the practice)
  const [activeDigId, setActiveDigId] = useState<DigId | null>(null)
  const [trenchShards, setTrenchShards] = useState<Shard[]>([])
  const [lifted, setLifted] = useState<Record<string, boolean>>({}) // shardId -> is in the lab
  const [interps, setInterps] = useState<Interpretation[]>([])
  const [currentSpecimen, setCurrentSpecimen] = useState<Shard | null>(null)
  const [distances, setDistances] = useState<DistanceVector>(DEFAULT_DIST)
  const [gloss, setGloss] = useState('')
  const [counterGloss, setCounterGloss] = useState('')
  const [nuanceTags, setNuanceTags] = useState<NuanceTag[]>([])
  const [confidence, setConfidence] = useState(3)
  const [specimenStart, setSpecimenStart] = useState<number>(0)

  // Cluster state (explicitly a proposal)
  const [cluster, setCluster] = useState<ClusterProposal | null>(null)
  const [manualClusterEdits, setManualClusterEdits] = useState(0)
  const [boundaryMarks, setBoundaryMarks] = useState<Set<string>>(new Set())

  // Telemetry
  const { events, record, recordWithLatency, flush, getPath, getSkipCount } = useDigTelemetry({
    digId: activeDigId,
    onBatch: async (batch) => {
      if (!activeDigId) return
      try {
        await fetch('/api/dig/event', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ siteId, digId: activeDigId, events: batch })
        })
      } catch { /* best effort; local is still captured */ }
    }
  })

  // Calibration (baseline)
  const [calibration, setCalibration] = useState<SiteCalibration>({
    defaultTemporal: 6, defaultSpatial: 7, defaultSocial: 5, defaultHypothetical: 4,
    recurringRituals: ['news doomscroll', 'travel research']
  })
  const [calOpen, setCalOpen] = useState(false)

  // Archive
  const [pastDigs, setPastDigs] = useState<any[]>([])
  const [exports, setExports] = useState<any[]>([])

  const digStartRef = useRef<number>(0)

  // Load past digs on mount (from local + server)
  useEffect(() => {
    const local = localStorage.getItem('strata_past_digs')
    if (local) setPastDigs(JSON.parse(local))
    // Fire-and-forget server fetch for this site
    fetch(`/api/dig/mine?site=${siteId}`).then(r => r.json()).then(d => {
      if (d?.digs?.length) {
        setPastDigs(d.digs)
        localStorage.setItem('strata_past_digs', JSON.stringify(d.digs))
      }
    }).catch(() => {})
  }, [siteId])

  // ===== Core practice actions =====
  function startNewDig() {
    const digId = 'dg_' + crypto.randomUUID().slice(0, 12)
    setActiveDigId(digId)
    digStartRef.current = Date.now()

    // Select a balanced trench: 3 surface + 3 recent + 2 deep + 1 fossil (9 total for focus)
    const shuffled = [...SHARDS].sort(() => Math.random() - 0.5)
    const trench = [
      ...shuffled.filter(s => s.stratum === 0).slice(0, 3),
      ...shuffled.filter(s => s.stratum === 1).slice(0, 3),
      ...shuffled.filter(s => s.stratum === 2).slice(0, 2),
      ...shuffled.filter(s => s.stratum === 3).slice(0, 1),
    ].sort((a, b) => a.stratum - b.stratum)

    setTrenchShards(trench)
    setLifted({})
    setInterps([])
    setCluster(null)
    setManualClusterEdits(0)
    setBoundaryMarks(new Set())
    setCurrentSpecimen(null)
    setDistances({ ...DEFAULT_DIST })
    setGloss(''); setCounterGloss(''); setNuanceTags([]); setConfidence(3)

    record('dig_started', { 
      trenchSize: trench.length, 
      shardIds: trench.map(s => s.id),
      baseline: calibration 
    })

    setCurrentView('trench')
    toast.success('Trench opened. Begin excavation.')
  }

  function excavateLayer(stratum: number) {
    record('excavate_layer', { stratum, method: 'manual' })
    // Lift any not-yet-lifted from this stratum into the lab tray
    const fromLayer = trenchShards.filter(s => s.stratum === stratum && !lifted[s.id])
    if (fromLayer.length === 0) return
    const nextLifted = { ...lifted }
    fromLayer.forEach(s => { nextLifted[s.id] = true })
    setLifted(nextLifted)
    record('lifted_from_layer', { stratum, count: fromLayer.length })
  }

  function liftArtifact(shard: Shard) {
    const start = Date.now()
    setLifted(prev => ({ ...prev, [shard.id]: true }))
    setCurrentSpecimen(shard)
    setDistances({ ...DEFAULT_DIST })
    setGloss(''); setCounterGloss(''); setNuanceTags([]); setConfidence(3)
    setSpecimenStart(start)
    recordWithLatency('lift_artifact', { shardId: shard.id, title: shard.title, stratum: shard.stratum }, start)
  }

  function updateDistance(dim: keyof DistanceVector, val: number) {
    const old = distances[dim]
    const next = { ...distances, [dim]: clamp(val) }
    setDistances(next)
    record('adjust_distance', { 
      shardId: currentSpecimen?.id, 
      dim, 
      from: old, 
      to: next[dim],
      delta: next[dim] - old 
    })
  }

  function toggleNuance(tag: NuanceTag) {
    const isActive = nuanceTags.includes(tag)
    const next = isActive ? nuanceTags.filter(t => t !== tag) : [...nuanceTags, tag]
    setNuanceTags(next)
    record('toggle_nuance', { shardId: currentSpecimen?.id, tag, active: !isActive })
  }

  function commitInterpretation() {
    if (!activeDigId || !currentSpecimen) return
    const timeSpent = Date.now() - specimenStart
    if (gloss.trim().length < 12) {
      toast.error('A charitable gloss of at least a sentence helps the practice.')
      return
    }

    const interp: Interpretation = {
      id: 'int_' + crypto.randomUUID().slice(0, 10),
      digId: activeDigId,
      shardId: currentSpecimen.id,
      distances: { ...distances },
      gloss: gloss.trim(),
      counterGloss: counterGloss.trim() || undefined,
      nuanceTags: [...nuanceTags],
      confidence,
      timeSpentMs: timeSpent,
      createdAt: new Date().toISOString(),
    }

    const nextInterps = [...interps, interp]
    setInterps(nextInterps)

    record('commit_interp', {
      shardId: currentSpecimen.id,
      timeSpentMs: timeSpent,
      glossLen: gloss.trim().length,
      tagCount: nuanceTags.length,
      confidence,
      distances: distances
    })

    // Reset workbench for next specimen
    setCurrentSpecimen(null)
    setDistances({ ...DEFAULT_DIST })
    setGloss(''); setCounterGloss(''); setNuanceTags([]); setConfidence(3)

    // Auto-recompute live charity
    const score = computeInterpretiveCharityScore(nextInterps, cluster, manualClusterEdits, getSkipCount())
    if (nextInterps.length % 2 === 0) {
      toast.info(`Interpretive Charity: ${score.score}`, { duration: 1400 })
    }
  }

  function skipArtifact(shard: Shard, reason = 'left in situ') {
    setLifted(prev => ({ ...prev, [shard.id]: true })) // still "lifted" for trench completeness
    record('skip_artifact', { shardId: shard.id, reason })
    if (currentSpecimen?.id === shard.id) {
      setCurrentSpecimen(null)
      setDistances({ ...DEFAULT_DIST })
      setGloss(''); setCounterGloss(''); setNuanceTags([])
    }
    toast('Left in the trench for now.')
  }

  // ===== Semantic clustering (the explicit "without erasing nuance" part) =====
  async function runSieve() {
    if (!activeDigId || interps.length < 3) {
      toast.error('Lift and interpret at least three specimens before running the sieve.')
      return
    }
    const start = Date.now()
    record('run_sieve', { interpCount: interps.length })

    try {
      const res = await fetch('/api/cluster', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          siteId,
          digId: activeDigId,
          interps: interps.map(i => ({
            shardId: i.shardId,
            gloss: i.gloss,
            tags: i.nuanceTags,
            distances: i.distances
          }))
        })
      })
      const data = await res.json()
      if (data?.proposal) {
        setCluster(data.proposal)
        setManualClusterEdits(0)
        setBoundaryMarks(new Set(data.proposal.boundaryCandidates || []))
        recordWithLatency('cluster_proposal_received', { 
          clusters: data.proposal.clusters?.length,
          boundaries: data.proposal.boundaryCandidates?.length 
        }, start)
        toast.success('Granulometry complete. Review the proposal.')
      } else {
        // Client fallback (still demonstrates the principle)
        const fallback = buildClientClusterProposal(activeDigId, interps)
        setCluster(fallback)
        setBoundaryMarks(new Set(fallback.boundaryCandidates))
        toast('Edge model quiet — used local cosine on tags + gloss keywords. Same principle.')
      }
    } catch {
      const fallback = buildClientClusterProposal(activeDigId, interps)
      setCluster(fallback)
      setBoundaryMarks(new Set(fallback.boundaryCandidates))
      toast('Using local clustering (same rules).')
    }
  }

  function markBoundary(shardId: string) {
    const next = new Set(boundaryMarks)
    if (next.has(shardId)) next.delete(shardId)
    else next.add(shardId)
    setBoundaryMarks(next)
    setManualClusterEdits(m => m + 1)
    record('toggle_boundary', { shardId, nowBoundary: !boundaryMarks.has(shardId) })
  }

  function moveToCluster(shardId: string, toLabel: string) {
    if (!cluster) return
    // Simulate edit — in real impl would persist user-defined clusters
    setManualClusterEdits(m => m + 1)
    record('manual_cluster_move', { shardId, to: toLabel })
    toast('Moved. This edit contributes to charity.')
  }

  function acceptClustering() {
    if (!cluster || !activeDigId) return
    const finalBoundaries = Array.from(boundaryMarks)
    record('accept_clustering', { edits: manualClusterEdits, boundaries: finalBoundaries.length })

    const score = computeInterpretiveCharityScore(interps, cluster, manualClusterEdits, getSkipCount())
    const poverty = computeRitualPovertySig(interps, trenchShards.length)

    const summary = {
      interpretiveCharityScore: score.score,
      meanDistanceShift: computeMeanShift(interps),
      nuanceCount: interps.reduce((s, i) => s + i.nuanceTags.length, 0),
      boundaryMarks: finalBoundaries.length,
      clusterEdits: manualClusterEdits,
      skips: getSkipCount(),
      totalEvents: events.length,
      ritualPovertySig: poverty,
    }

    // Persist dig
    const ended = new Date().toISOString()
    const digRecord = {
      id: activeDigId,
      siteId,
      startedAt: new Date(digStartRef.current).toISOString(),
      endedAt: ended,
      shardIds: trenchShards.map(s => s.id),
      summary,
      calibration,
    }

    // Save locally + try server
    const nextPast = [digRecord, ...pastDigs.filter(d => d.id !== activeDigId)].slice(0, 24)
    setPastDigs(nextPast)
    localStorage.setItem('strata_past_digs', JSON.stringify(nextPast))

    fetch('/api/dig/complete', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ siteId, dig: digRecord, interps, events: events.slice(-80), cluster })
    }).catch(() => {})

    flush()
    setActiveDigId(null)
    setCluster(null)
    setCurrentView('archive')
    toast.success(`Dig closed. Interpretive Charity ${score.score}.`)
  }

  function endDigEarly() {
    if (!activeDigId) return
    if (!confirm('Close the trench without final clustering? Your logged work is still saved.')) return
    flush()
    setActiveDigId(null)
    setCurrentView('archive')
  }

  // ===== Live computed =====
  const liveCharity = useMemo(() => 
    computeInterpretiveCharityScore(interps, cluster, manualClusterEdits, getSkipCount()), 
    [interps, cluster, manualClusterEdits, events]
  )

  const liftedShards = useMemo(() => trenchShards.filter(s => lifted[s.id]), [trenchShards, lifted])
  const unliftedByStratum = useMemo(() => {
    const m: Record<number, Shard[]> = {0:[],1:[],2:[],3:[]}
    trenchShards.forEach(s => { if (!lifted[s.id]) m[s.stratum].push(s) })
    return m
  }, [trenchShards, lifted])

  // Current reframed text for the specimen (the actual psychological distance practice)
  const reframedText = useMemo(() => {
    if (!currentSpecimen) return ''
    const d = distances
    const r = currentSpecimen.reframings
    // Weighted blend — higher number pulls toward the distal/abstract pole
    const parts: string[] = []
    ;(Object.keys(d) as (keyof DistanceVector)[]).forEach(dim => {
      const v = d[dim]
      if (v <= 3) parts.push(r[dim].low)
      else if (v >= 8) parts.push(r[dim].high)
      else {
        // middle — show both with connector
        parts.push(`${r[dim].low}  /  ${r[dim].high}`)
      }
    })
    return parts.join('\n\n')
  }, [currentSpecimen, distances])

  // ===== Archive + export controls =====
  async function requestPrivateExport() {
    try {
      const res = await fetch('/api/export/request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ siteId, includeEvents: true })
      })
      const data = await res.json()
      if (data?.exportId) {
        toast.success('Private export queued to R2. Check Controls tab.')
        loadExports()
      }
    } catch {
      toast.error('Could not queue export (offline or server). Your local data is still here.')
    }
  }

  async function loadExports() {
    try {
      const r = await fetch(`/api/exports?site=${siteId}`)
      const j = await r.json()
      setExports(j?.exports || [])
    } catch {}
  }

  async function hardDeleteSite() {
    if (!confirm('This will permanently delete every dig, interpretation, event, and R2 export for this private site key. Irreversible. Type the last 6 chars to confirm in the next prompt.')) return
    const confirmCode = siteId.slice(-6)
    const typed = prompt(`Type the last 6 characters of your site key to confirm: ${confirmCode}`)
    if (typed !== confirmCode) { toast.error('Delete cancelled.'); return }

    try {
      await fetch('/api/site/delete', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ siteId })
      })
    } catch {}
    localStorage.removeItem('strata_site_id')
    localStorage.removeItem('strata_past_digs')
    window.location.reload()
  }

  // ===== Render =====
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] selection:bg-[var(--accent)]/30">
      <TopNav siteId={siteId} currentView={currentView} setCurrentView={setCurrentView} onNewDig={startNewDig} />

      <main className="max-w-[1120px] mx-auto px-5 pb-24">
        {currentView === 'hero' && (
          <Hero onStart={startNewDig} onCal={() => setCalOpen(true)} siteId={siteId} />
        )}

        {currentView === 'trench' && activeDigId && (
          <div className="pt-8">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="uppercase tracking-[2px] text-xs text-[var(--accent)]">ACTIVE TRENCH</div>
                <div className="text-3xl font-semibold tracking-[-1.1px] mt-1">Attention archaeology in progress</div>
              </div>
              <div className="flex gap-2">
                <button onClick={runSieve} className="btn flex items-center gap-2"><Layers size={15} /> Run semantic sieve</button>
                <button onClick={endDigEarly} className="btn btn-ghost">Close trench</button>
              </div>
            </div>

            {/* Live Charity + Principle */}
            <div className="mb-6 grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3 card p-5">
                <div className="flex items-baseline justify-between">
                  <div className="section-title">Interpretive Charity (live)</div>
                  <div className="text-[10px] text-[var(--text-subtle)]">Higher = more distance practiced + more nuance defended</div>
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <div className="score text-7xl font-semibold tabular-nums tracking-[-3.2px] text-[var(--accent)]">{liveCharity.score}</div>
                  <div className="text-sm text-[var(--text-muted)] max-w-[28ch]">out of ~96. The number moves when you shift distance or refuse easy clusters.</div>
                </div>
                <div className="mt-4 text-[11px] text-[var(--text-subtle)]">{CLUSTERING_PRINCIPLE}</div>
              </div>
              <div className="lg:col-span-2 card p-5 text-sm">
                <div className="section-title mb-2">What the score sees</div>
                <ul className="charity-breakdown text-[var(--text-muted)]">
                  <li>Distance practice: {liveCharity.distancePractice}</li>
                  <li>Nuance tags + counter-reads: {liveCharity.nuanceWork}</li>
                  <li>Boundary defence: {liveCharity.boundaryWork}</li>
                  <li>Cluster courage (edits): {liveCharity.clusterCourage}</li>
                  <li>Reflection depth: {liveCharity.reflectionDepth}</li>
                </ul>
                <div className="mt-3 text-[10px] text-[var(--text-subtle)]">Skips logged: {getSkipCount()} (thoughtful skips rarely hurt)</div>
              </div>
            </div>

            {/* The Trench — strata excavation */}
            <div className="mb-6">
              <div className="section-title mb-2 flex items-center gap-2"><Pickaxe size={14} /> THE TELL — click strata to excavate</div>
              <div className="strata-column relative rounded-xl p-4 md:p-6 overflow-hidden" style={{ minHeight: 168 }}>
                <div className="sediment" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 relative z-10">
                  {[0,1,2,3].map(stratum => {
                    const label = ['Surface (recent 7d)', 'Recent (weeks–months)', 'Deep (1–2 yr)', 'Fossil (>2 yr)'][stratum]
                    const remaining = unliftedByStratum[stratum].length
                    const totalInLayer = trenchShards.filter(s => s.stratum === stratum).length
                    return (
                      <div key={stratum} className="stratum card p-4 bg-[var(--bg-trench)] border-[var(--border)]" onClick={() => excavateLayer(stratum)}>
                        <div className="text-[10px] text-[var(--text-subtle)]">{label}</div>
                        <div className="mt-1 text-lg font-medium tracking-tight">{remaining} / {totalInLayer} remaining</div>
                        <div className="mt-3 text-xs text-[var(--accent)]">Excavate layer →</div>
                        {remaining > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {unliftedByStratum[stratum].slice(0, 2).map(s => (
                              <button key={s.id} onClick={(e) => { e.stopPropagation(); liftArtifact(s) }} className="text-[10px] px-2 py-0.5 rounded bg-[var(--bg-surface)] border border-[var(--border)] hover:border-[var(--accent)]">{s.title}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* The Lab / Workbench + Lifted tray */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Left: lifted specimens tray + current workbench */}
              <div className="lg:col-span-7 space-y-5">
                <div>
                  <div className="section-title mb-2">FINDS TRAY — specimens lifted for interpretation</div>
                  <div className="flex flex-wrap gap-2">
                    {liftedShards.length === 0 && <div className="text-sm text-[var(--text-subtle)]">Nothing lifted yet. Excavate a layer above.</div>}
                    {liftedShards.map(sh => {
                      const done = interps.some(i => i.shardId === sh.id)
                      return (
                        <button key={sh.id} onClick={() => liftArtifact(sh)} className={`artifact specimen px-3 py-1.5 text-sm rounded border ${done ? 'opacity-60' : ''} ${currentSpecimen?.id === sh.id ? 'artifact-lifted border-[var(--accent)]' : ''}`}>
                          {sh.title} {done && <Check size={13} className="inline ml-1" />}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* The actual psychological distance practice surface */}
                <AnimatePresence>
                  {currentSpecimen && (
                    <motion.div initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} className="specimen p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-xs text-[var(--text-subtle)]">RECOVERED FRAGMENT • STRATUM {currentSpecimen.stratum}</div>
                          <div className="font-medium text-xl tracking-tight mt-0.5">{currentSpecimen.title}</div>
                        </div>
                        <button onClick={() => skipArtifact(currentSpecimen)} className="btn btn-ghost text-xs">Leave in situ</button>
                      </div>

                      <div className="mt-4 p-4 bg-[var(--bg-trench)] border border-[var(--border)] rounded text-[13.5px] leading-snug font-archival whitespace-pre-wrap">
                        {currentSpecimen.text}
                      </div>

                      {/* The four distance dials — core cognitive move UI */}
                      <div className="mt-5">
                        <div className="section-title mb-2">PRACTICE PSYCHOLOGICAL DISTANCE — move the dials, feel the reframing</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                          {(['temporal','spatial','social','hypothetical'] as const).map(dim => (
                            <div key={dim} className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <div className="dial-label">{dim.toUpperCase()}</div>
                                <div className="tabular-nums font-mono text-[var(--accent)]">{distances[dim]} / 10</div>
                              </div>
                              <input 
                                type="range" min={0} max={10} step={1}
                                value={distances[dim]}
                                onChange={e => updateDistance(dim, parseInt(e.target.value))}
                                className="dial w-full accent-[var(--accent)]"
                              />
                              <div className="text-[10px] text-[var(--text-subtle)] flex justify-between">
                                <div>Proximal / concrete</div>
                                <div>Distal / abstract</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Live reframing — this is the move made visible */}
                      {reframedText && (
                        <div className="mt-5 p-4 border-l-2 border-[var(--accent-dim)] bg-black/20 text-[13px] font-archival leading-snug">
                          {reframedText}
                        </div>
                      )}

                      {/* Nuance tools + gloss (charitable interpretation) */}
                      <div className="mt-6">
                        <div className="section-title mb-2">NUANCE PALETTE — tools for not erasing complexity</div>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {ALL_NUANCE_TAGS.map(tag => (
                            <button key={tag} onClick={() => toggleNuance(tag)} className={`tag ${nuanceTags.includes(tag) ? 'active' : ''}`}>{tag}</button>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-[var(--text-subtle)] mb-1">CHARITABLE GLOSS (what generous reading is available here?)</div>
                            <textarea 
                              value={gloss} onChange={e => setGloss(e.target.value)} 
                              className="w-full h-20 bg-[var(--bg-trench)] border border-[var(--border)] rounded p-2.5 text-sm font-archival resize-y"
                              placeholder="Even if the tab was closed, the act of opening both flight and train sites shows the conflict was live for at least 17 minutes…"
                            />
                          </div>
                          <div>
                            <div className="text-xs text-[var(--text-subtle)] mb-1">COUNTER-READ (the harshest plausible story — surface it)</div>
                            <textarea 
                              value={counterGloss} onChange={e => setCounterGloss(e.target.value)} 
                              className="w-full h-20 bg-[var(--bg-trench)] border border-[var(--border)] rounded p-2.5 text-sm font-archival resize-y"
                              placeholder="Pure moral licensing theatre to feel better about the eventual flight."
                            />
                          </div>
                        </div>

                        <div className="mt-3 flex items-center gap-4">
                          <div>
                            <div className="text-xs text-[var(--text-subtle)] mb-1">CONFIDENCE IN THIS READING</div>
                            <input type="range" min={1} max={5} value={confidence} onChange={e => setConfidence(parseInt(e.target.value))} className="accent-[var(--data)] w-40" />
                            <span className="ml-2 tabular-nums text-sm">{confidence}</span>
                          </div>
                          <button onClick={commitInterpretation} className="btn btn-primary flex items-center gap-2 mt-5"><Check size={15} /> Log this interpretation</button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Right: live ledger + cluster panel */}
              <div className="lg:col-span-5 space-y-5">
                <div className="card p-4">
                  <div className="section-title mb-2 flex items-center gap-2"><BarChart3 size={14} /> LIVE ATTENTION LEDGER</div>
                  <div className="ledger max-h-[218px] overflow-auto p-2 text-[10px]">
                    {events.slice().reverse().slice(0, 18).map((e, idx) => (
                      <div key={idx} className="ledger-entry flex gap-2">
                        <span className="text-[var(--text-subtle)] tabular-nums w-[42px] shrink-0">+{(e.t / 1000).toFixed(1)}s</span>
                        <span className="font-medium text-[var(--text)]">{e.type}</span>
                        <span className="opacity-70 truncate">{JSON.stringify(e.payload).slice(0, 92)}</span>
                      </div>
                    ))}
                    {events.length === 0 && <div className="text-[var(--text-subtle)] p-2">Every action here is captured. Latency, path, skips, reflection time.</div>}
                  </div>
                  <div className="text-[10px] text-[var(--text-subtle)] mt-2">Path length: {getPath().length} • Skips: {getSkipCount()}</div>
                </div>

                {/* Cluster / Sieve panel — the explicit design point */}
                <div className="card p-4">
                  <div className="section-title mb-2 flex items-center justify-between">
                    <span>SEMANTIC SIEVE — clustering as provocation only</span>
                    {cluster && <button onClick={acceptClustering} className="btn btn-primary text-xs">Accept &amp; close dig</button>}
                  </div>

                  {!cluster && <div className="text-sm text-[var(--text-muted)]">Interpret 3+ specimens, then run the sieve. The resulting clusters are proposals. Your manual boundary work and overrides are what the charity score rewards.</div>}

                  {cluster && (
                    <div className="space-y-4 text-sm">
                      {cluster.clusters.map((c, i) => (
                        <div key={i} className="cluster p-3">
                          <div className="cluster-header">{c.label} <span className="opacity-50">• affinity {c.avgAffinity.toFixed(2)}</span></div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {c.members.map(mid => {
                              const s = getShard(mid)
                              const isBoundary = boundaryMarks.has(mid)
                              return (
                                <div key={mid} className={`px-2 py-0.5 rounded text-xs border flex items-center gap-1 ${isBoundary ? 'boundary' : 'bg-[var(--bg-surface)] border-[var(--border)]'}`}>
                                  {s?.title || mid}
                                  <button onClick={() => markBoundary(mid)} className="ml-1 text-[9px] opacity-70 hover:opacity-100">{isBoundary ? 'unmark' : 'boundary?'}</button>
                                  <button onClick={() => moveToCluster(mid, c.label)} className="ml-0.5 text-[9px] opacity-70 hover:opacity-100"><Move size={11} /></button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                      <div className="text-[10px] text-[var(--accent)]">Edits so far: {manualClusterEdits}. Every manual boundary or move increases charity.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'archive' && (
          <ArchiveView pastDigs={pastDigs} onNew={() => { startNewDig() }} />
        )}

        {currentView === 'about' && <AboutBlueprint />}

        {currentView === 'controls' && (
          <ControlsView 
            siteId={siteId} 
            exports={exports} 
            onRequestExport={requestPrivateExport} 
            onDelete={hardDeleteSite} 
            onLoadExports={loadExports}
          />
        )}
      </main>

      <footer className="border-t border-[var(--border)] py-8 text-xs text-[var(--text-subtle)]">
        <div className="max-w-[1120px] mx-auto px-5 flex flex-col md:flex-row gap-y-2 md:items-center justify-between">
          <div>STRATA — practice psychological distance. Attention archaeology for climate digital rituals. Not a journal.</div>
          <CreatorLinks variant="footer" />
        </div>
      </footer>

      {/* Calibration modal */}
      <AnimatePresence>
        {calOpen && (
          <CalibrationModal calibration={calibration} setCalibration={setCalibration} onClose={() => setCalOpen(false)} />
        )}
      </AnimatePresence>
    </div>
  )
}

// ===== Supporting screens and components =====

function TopNav({ siteId, currentView, setCurrentView, onNewDig }: any) {
  const items = [
    { id: 'hero', label: 'Site' },
    { id: 'trench', label: 'Open Trench' },
    { id: 'archive', label: 'Archive' },
    { id: 'about', label: 'Method' },
    { id: 'controls', label: 'Data Controls' },
  ]
  return (
    <nav className="sticky top-0 z-50 border-b border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur">
      <div className="max-w-[1120px] mx-auto px-5 h-14 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <Link to="/" onClick={() => setCurrentView('hero')} className="font-semibold tracking-[-0.6px] text-xl flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--accent)]" /> STRATA
          </Link>
          <div className="hidden md:flex items-center gap-4 text-sm">
            {items.map(it => (
              <button key={it.id} onClick={() => setCurrentView(it.id as any)} className={`${currentView === it.id ? 'text-[var(--accent)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}>
                {it.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onNewDig} className="btn btn-primary flex items-center gap-2 text-sm"><Play size={15} /> New dig</button>
          <div className="text-[10px] font-mono text-[var(--text-subtle)] hidden lg:block">{siteId}</div>
          <CreatorLinks variant="compact" />
        </div>
      </div>
    </nav>
  )
}

function Hero({ onStart, onCal, siteId }: any) {
  return (
    <div className="pt-14 pb-12 max-w-3xl">
      <div className="uppercase tracking-[3.5px] text-xs text-[var(--accent)]">ATTENTION ARCHAEOLOGY • COGNITIVE MOVE: PSYCHOLOGICAL DISTANCE</div>
      <h1 className="text-[68px] leading-[0.92] font-semibold tracking-[-4.2px] mt-3">You already know the climate facts.<br />The rituals are the problem.</h1>
      <p className="mt-6 text-[17px] text-[var(--text-muted)] max-w-prose">
        Digital life is full of thin, conflicted micro-rituals around climate-relevant choices — research that goes nowhere, offsets that soothe, comment threads that replace reading. 
        STRATA is not a journal. It is a deliberate excavation practice that trains you to move psychological distance on purpose: temporal, spatial, social, hypothetical.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={onStart} className="btn btn-primary px-7 py-2.5 text-[15px]">Open a trench — start a dig</button>
        <button onClick={onCal} className="btn px-6">Calibrate my baseline distance</button>
      </div>

      <div className="mt-10 text-xs max-w-prose border-l-2 border-[var(--border)] pl-4 text-[var(--text-subtle)]">
        Every action — latency between dials, gloss length, which layers you skip, how many times you mark something “boundary” — is captured as the real data. 
        Semantic clustering (edge embeddings) is used only to propose groupings. Your manual defence of nuance and ambiguity is what produces the Interpretive Charity score.
      </div>

      <div className="mt-16">
        <CreatorLinks variant="hero" />
      </div>

      <div className="mt-8 text-[10px] text-[var(--text-subtle)] font-mono">Private site key: {siteId} (never sent to any third party; your sovereign identifier)</div>
    </div>
  )
}

function CalibrationModal({ calibration, setCalibration, onClose }: any) {
  const [c, setC] = useState(calibration)
  return (
    <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-5" onClick={onClose}>
      <div className="card w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="font-medium text-lg tracking-tight">Site calibration — your default psychological distance on climate topics</div>
        <p className="text-sm text-[var(--text-muted)] mt-1">This baseline lets later digs show whether you actually moved distance during the practice. Private to your site key.</p>

        <div className="mt-6 space-y-5">
          {(['temporal','spatial','social','hypothetical'] as const).map(dim => (
            <div key={dim}>
              <div className="flex justify-between text-sm mb-1.5">
                <div>{dim}</div>
                <div className="tabular-nums text-[var(--accent)]">{(c as any)[`default${dim[0].toUpperCase()+dim.slice(1)}`]}/10</div>
              </div>
              <input type="range" min={0} max={10} value={(c as any)[`default${dim[0].toUpperCase()+dim.slice(1)}`]} onChange={e => setC({ ...c, [`default${dim[0].toUpperCase()+dim.slice(1)}`]: parseInt(e.target.value) })} className="w-full accent-[var(--accent)]" />
            </div>
          ))}
        </div>

        <div className="mt-6">
          <div className="text-xs text-[var(--text-subtle)] mb-1.5">TWO RECURRING DIGITAL RITUALS YOU NOTICE IN YOURSELF</div>
          <input className="w-full bg-[var(--bg-trench)] border border-[var(--border)] rounded p-2 text-sm" value={c.recurringRituals.join(', ')} onChange={e => setC({ ...c, recurringRituals: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={() => { setCalibration(c); onClose() }} className="btn btn-primary flex-1">Save baseline</button>
          <button onClick={onClose} className="btn flex-1">Cancel</button>
        </div>
        <div className="text-[10px] text-[var(--text-subtle)] mt-4">Changing this later does not retroactively alter past dig scores.</div>
      </div>
    </div>
  )
}

function ArchiveView({ pastDigs, onNew }: any) {
  const has = pastDigs.length > 0
  const avgCharity = has ? Math.round(pastDigs.reduce((s: number, d: any) => s + (d.summary?.interpretiveCharityScore || 0), 0) / pastDigs.length) : 0

  return (
    <div className="pt-9">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-title">YOUR STRATIGRAPHIC RECORD</div>
          <div className="text-4xl font-semibold tracking-tight mt-1">Past digs</div>
        </div>
        <button onClick={onNew} className="btn">New trench</button>
      </div>

      {!has && <div className="mt-10 card p-8 text-[var(--text-muted)]">No completed digs yet. Open a trench and log interpretations to begin building a portrait of your distance practice.</div>}

      {has && (
        <>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card p-4"><div className="text-xs text-[var(--text-subtle)]">Digs closed</div><div className="text-4xl font-semibold tabular-nums mt-1">{pastDigs.length}</div></div>
            <div className="card p-4"><div className="text-xs text-[var(--text-subtle)]">Avg interpretive charity</div><div className="text-4xl font-semibold tabular-nums mt-1 text-[var(--accent)]">{avgCharity}</div></div>
          </div>

          <div className="mt-8 space-y-3">
            {pastDigs.map((d: any) => {
              const s = d.summary || {}
              return (
                <div key={d.id} className="card p-4 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div>
                    <div className="font-medium tabular-nums">{format(new Date(d.startedAt), 'dd MMM yyyy • HH:mm')}</div>
                    <div className="text-xs text-[var(--text-muted)]">{d.shardIds?.length || 0} specimens • {s.totalEvents || 0} events captured</div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div>Charity <span className="font-semibold tabular-nums text-[var(--accent)]">{s.interpretiveCharityScore}</span></div>
                    <div>Boundaries <span className="font-semibold tabular-nums">{s.boundaryMarks}</span></div>
                    <div>Edits <span className="font-semibold tabular-nums">{s.clusterEdits}</span></div>
                    <div className="text-[var(--text-subtle)] text-xs">Ritual poverty sig {s.ritualPovertySig}</div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="mt-8 text-xs max-w-prose text-[var(--text-subtle)]">Trends in distance movement and cluster courage appear after 3–4 digs. The data belongs to the private site key only.</div>
        </>
      )}
    </div>
  )
}

function ControlsView({ exports, onRequestExport, onDelete, onLoadExports }: any) {
  useEffect(() => { onLoadExports() }, [])
  return (
    <div className="pt-9 max-w-2xl">
      <div className="uppercase tracking-[2px] text-xs text-[var(--accent)] mb-1">SOVEREIGN DATA</div>
      <h2 className="text-3xl font-semibold tracking-tight">Private export &amp; delete controls</h2>
      <p className="text-[var(--text-muted)] mt-2">All data for this site key lives in D1, KV, and R2 under your control. Exports are written to a private R2 prefix and can be deleted.</p>

      <div className="mt-7 card p-5">
        <div className="font-medium mb-1">Request full private archive</div>
        <div className="text-sm text-[var(--text-muted)]">Triggers a Workflow that assembles every dig, interpretation, telemetry event, and cluster for this site and writes a timestamped object to R2. You receive a download link via the worker.</div>
        <button onClick={onRequestExport} className="btn mt-4 flex items-center gap-2"><Download size={15} /> Request R2 export (Workflow + R2)</button>
      </div>

      <div className="mt-5">
        <div className="section-title mb-2">Recent exports (R2)</div>
        {exports.length === 0 && <div className="text-sm text-[var(--text-muted)]">None yet. Request one above.</div>}
        {exports.map((e: any, i: number) => (
          <div key={i} className="card p-3 text-sm flex justify-between items-center mb-2">
            <div>{e.key || e.id}</div>
            <a className="btn btn-sm" href={e.url || '#'} target="_blank" rel="noreferrer">Download</a>
          </div>
        ))}
      </div>

      <div className="mt-9 border-t border-[var(--border)] pt-6">
        <div className="font-medium text-[#c88f7a]">Hard delete this site</div>
        <div className="text-sm text-[var(--text-muted)] mt-1">Removes all rows from D1 for this site, purges KV keys, and deletes all R2 objects under the site prefix. Irreversible. Use when you want the ritual trace gone.</div>
        <button onClick={onDelete} className="btn btn-danger mt-4 flex items-center gap-2"><Trash2 size={15} /> Permanently delete everything for this site key</button>
      </div>

      <div className="mt-10 text-[10px] text-[var(--text-subtle)]">Email Routing is configured for community shard contributions (see README). Workflows handle async export assembly.</div>
      <CreatorLinks variant="controls" />
    </div>
  )
}

function AboutBlueprint() {
  return (
    <div className="pt-9 max-w-3xl prose prose-invert text-[15px] text-[var(--text-muted)]">
      <h2 className="text-[var(--text)] tracking-tight text-3xl font-semibold">Blueprint &amp; rationale</h2>
      <p>STRATA exists to let people practice one specific cognitive move they usually lack when making or noticing climate-relevant choices inside digital life: the calibrated use of psychological distance.</p>

      <h3 className="text-[var(--text)] mt-8">Ritual poverty</h3>
      <p>Most climate-adjacent digital behaviour is thin: research that never becomes action, virtue signals that are immediately contradicted, doomscrolling that produces affect but no durable change in material throughput. These are rituals — repeated, low-friction, emotionally regulating — but they are poor ones. They do not create the conditions for different choices later.</p>

      <h3 className="text-[var(--text)] mt-8">The move: psychological distance</h3>
      <p>Construal level theory identifies four dimensions: temporal, spatial, social, hypothetical. Climate impacts are almost always construed as distant on all four. The app does not tell you “think globally”. It gives you repeated, low-stakes, high-resolution practice at deliberately moving the dials on real, ambivalent fragments and noticing what changes in the meaning of the fragment when you do.</p>

      <h3 className="text-[var(--text)] mt-8">Why “attention archaeology” and not a journal</h3>
      <p>A journal would ask you to confess your own climate sins. That produces shame, performance, or avoidance. STRATA gives you a library of recovered, specific, non-caricatured digital climate micro-rituals (the “shards”). You excavate and interpret them. Your own data is the trace of the excavation — latency, path, skips, gloss depth, boundary marking — not the content of the original choices. This is cleaner, less self-protective, and still deeply personal.</p>

      <h3 className="text-[var(--text)] mt-8">Semantic clustering without erasing nuance (explicit)</h3>
      <p>When you have logged several interpretations we run embeddings (Workers AI bge-small) over the shard text + your gloss + your tags. We produce a proposal. The UI always surfaces low-affinity members and “boundary candidates”. Every time you mark something a boundary or manually move an item you increase the charity score. The design contract is written in the product: clustering is scaffolding for seeing structure; it is never allowed to become the verdict. This is stated in the live UI and in the score computation.</p>

      <h3 className="text-[var(--text)] mt-8">Captured signals (the real output)</h3>
      <ul>
        <li>Latency between every meaningful action (dial move, tag toggle, gloss typing start/end)</li>
        <li>Choice path (exact sequence of layers touched, specimens lifted, order of commits)</li>
        <li>Confidence self-report on each interpretation</li>
        <li>Skip behaviour (what you chose not to lift, how often)</li>
        <li>Reflection depth (gloss length + time on specimen + re-edits after seeing clusters)</li>
        <li>Distance vectors at moment of commit, and how far they moved from neutral</li>
        <li>Cluster edits and boundary marks (the charity signal)</li>
      </ul>

      <h3 className="text-[var(--text)] mt-8">Cloudflare stack on free tier</h3>
      <p>Pages for the SPA. Worker (Hono) for all API + email handler + workflow trigger. D1 for relational truth (sites, digs, shards, interps, events). KV for hot cache, export status, rate keys. R2 for private user exports with site-prefixed keys (delete is a prefix purge). Workflows for the heavier async export assembly (gather + write to R2). Email Routing for inbound community shard contributions (parse simple text, insert generalized shard). Workers AI for the embeddings that power the sieve. All of this is deliberately over-specified so the architecture itself demonstrates what an edge cognitive instrument can be.</p>

      <h3 className="text-[var(--text)] mt-8">Emotional register: conflicted ambition</h3>
      <p>The palette (deep umber, warm amber, clay, data teal) and the slow, slightly resistant interactions are meant to feel like real archaeological work: careful, a bit grim, occasionally beautiful, never triumphant. The ambition (to actually change the quality of attention we bring to these choices) is always in tension with the difficulty and the scale. The UI never lets you feel you have “solved” anything by completing a dig.</p>

      <div className="mt-10 text-xs text-[var(--text-subtle)] border-t border-[var(--border)] pt-6">This is a complete, functional starter. The server side implements real D1 persistence, real AI clustering when available, real R2 export via Workflow, real delete, and the email contribution path. Run it, dig in it, delete it, fork the method.</div>
      <CreatorLinks variant="hero" />
    </div>
  )
}

// ===== Mean shift helper (stub for summary; was missing causing tsc fail) =====
function computeMeanShift(interps: Interpretation[]): Partial<DistanceVector> {
  if (interps.length === 0) return {}
  const dims: (keyof DistanceVector)[] = ['temporal', 'spatial', 'social', 'hypothetical']
  const sums: Record<keyof DistanceVector, number> = { temporal: 0, spatial: 0, social: 0, hypothetical: 0 }
  interps.forEach(i => {
    dims.forEach(d => { sums[d] += i.distances[d] - 5 })
  })
  const n = interps.length
  const res: Partial<DistanceVector> = {}
  dims.forEach(d => { res[d] = Math.round((sums[d] / n) * 10) / 10 })
  return res
}

// ===== Client fallback clustering (same contract as server) =====
function buildClientClusterProposal(digId: string, interps: Interpretation[]): ClusterProposal {
  // Extremely simple but honest: group by overlapping nuance tags + rough gloss keyword buckets.
  // Still forces boundary detection.
  const buckets: Record<string, string[]> = {}
  interps.forEach(i => {
    const key = (i.nuanceTags[0] || 'untyped') + '-' + (i.gloss.toLowerCase().includes('structure') ? 'struct' : 'other')
    if (!buckets[key]) buckets[key] = []
    buckets[key].push(i.shardId)
  })
  const clusters = Object.entries(buckets).map(([k, members], idx) => ({
    label: `Cluster ${String.fromCharCode(65 + idx)}: ${k.split('-')[0]}`,
    members,
    avgAffinity: 0.71 + (idx % 2) * 0.07
  }))
  // pick 1-2 boundary candidates (items that appeared in glosses with "both" or "and" language or low tag overlap)
  const boundaries = interps.filter(i => i.gloss.toLowerCase().includes('both') || i.gloss.toLowerCase().includes('and ') || i.nuanceTags.length <= 1).map(i => i.shardId).slice(0, 2)
  return {
    id: 'cl_' + Math.random().toString(36).slice(2, 9),
    digId,
    clusters,
    boundaryCandidates: boundaries,
    method: 'local-keyword + tag-overlap (fallback)',
    createdAt: new Date().toISOString()
  }
}
